import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  respondToMFAChallenge,
  getCurrentSession,
  getCurrentUserEmail,
  signOut as cognitoSignOut,
} from './cognito';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check existing session on mount
  useEffect(() => {
    getCurrentSession()
      .then(async (result) => {
        if (result) {
          setUser(result.user);
          setIsAuthenticated(true);
          const userEmail = await getCurrentUserEmail();
          setEmail(userEmail);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (emailInput, password) => {
    const result = await cognitoSignIn(emailInput, password);

    if (result.mfaRequired || result.mfaSetup) {
      // MFA challenge — caller should handle second step
      return result;
    }

    // Direct success (no MFA)
    setUser(result.user);
    setIsAuthenticated(true);
    const userEmail = await getCurrentUserEmail();
    setEmail(userEmail);
    return result;
  }, []);

  const submitMFA = useCallback(async (cognitoUser, code, mfaType) => {
    const result = await respondToMFAChallenge(cognitoUser, code, mfaType);
    setUser(result.user);
    setIsAuthenticated(true);
    const userEmail = await getCurrentUserEmail();
    setEmail(userEmail);
    return result;
  }, []);

  const register = useCallback(async ({ email: emailInput, password, invitationCode }) => {
    return cognitoSignUp({ email: emailInput, password, invitationCode });
  }, []);

  const confirmRegistration = useCallback(async (emailInput, code) => {
    return cognitoConfirmSignUp(emailInput, code);
  }, []);

  const logout = useCallback(() => {
    cognitoSignOut();
    setUser(null);
    setEmail(null);
    setIsAuthenticated(false);
  }, []);

  const value = {
    user,
    email,
    loading,
    isAuthenticated,
    login,
    submitMFA,
    register,
    confirmRegistration,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
